import { lazy, useEffect, useRef, useState } from 'react';
import qs from 'query-string';
import Tooltip from 'src/componentes/tooltip';
import { useIntl } from 'react-intl';

import List from 'src/componentes/list';
import ListColumn from 'src/componentes/list/list-column';
import LucideIcon from 'src/componentes/lucide-icon';
import HoverCard from 'src/componentes/hover-card';
import StatusBadge from 'src/componentes/StatusBadge';
import { capitalizeFirstLetter } from 'src/utils/string';
import { obterParametrosPorPrefixo } from 'src/utils/filtros-url';
import { getConfiguration } from 'src/services/commonRequests';
import history from 'src/history';
import { MODULOS, TIPO_INTERACAO_SOPHIE, COLUNAS } from 'src/utils/constants';
import Badge from 'src/componentes/Badge';

import Search from 'src/paginas/gestao-risco/risco/RiscoListView/SearchDrawer/SearchDrawer';
import SearchSchema from './SearchSchema';
import SearchSchemaModal from './SearchSchemaModal';
import { formatNumber } from 'src/utils/number';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import DropdownItemRisco from './DropdownItemRisco';
import http from 'src/services/httpService';
import LoadingContainer from 'src/componentes/loading-container';
import IconButton from 'src/componentes/icon-button';
import { SophieIcon } from 'src/componentes/icons';
import useAuth from 'src/hooks/useAuth';
import ComentarioCelula from '../components/ComentarioCelula';

const AssistenteVirtual = lazy(() =>
  import('src/components/IA/ChatGPT/AssistenteVirtual')
);
const RiscoEditView = lazy(() =>
  import('src/paginas/gestao-risco/risco/RiscoEditView')
);

const obterParametroUrl = (chave) => {
  const parametros = window.location && qs.parse(window.location.search);
  return parametros && parametros[chave] ? parametros[chave] : null;
};

const obterParametroUrlLista = (chave, decodificar = true) => {
  const valor = obterParametroUrl(chave);
  if (!valor) return null;
  return (decodificar ? decodeURIComponent(valor) : valor).split(',');
};

const obterQueryStringInicial = () => ({
  areaId: obterParametroUrlLista('areaId'),
  areaNome: obterParametroUrlLista('areaNome'),
  areasSubordinadas: obterParametroUrl('areasSubordinadas'),
  nome: obterParametroUrl('nome'),
  statusId: obterParametroUrl('statusId'),
  statusDescricao: obterParametroUrl('statusDescricao'),
  idIntegracao: obterParametroUrl('idIntegracao'),
  processoId: obterParametroUrlLista('processoId'),
  processoNome: obterParametroUrlLista('processoNome'),
  incluirSubprocessos: obterParametroUrl('incluirSubprocessos'),
  modeloRiscoId: obterParametroUrl('modeloRiscoId'),
  modeloRiscoDescricao: obterParametroUrl('modeloRiscoDescricao'),
  matrizRiscoId: obterParametroUrl('matrizRiscoId'),
  matrizRiscoDescricao: obterParametroUrl('matrizRiscoDescricao'),
  grauRiscoId: obterParametroUrl('grauRiscoId'),
  tipoRiscoId: obterParametroUrl('tipoRiscoId'),
  tipoRiscoDescricao: obterParametroUrl('tipoRiscoDescricao'),
  responsavelId: obterParametroUrl('responsavelId'),
  responsavelNome: obterParametroUrl('responsavelNome'),
  grausRiscoInerenteId: obterParametroUrlLista('grausRiscoInerenteId', false),
  grausRiscoInerenteNome: obterParametroUrlLista(
    'grausRiscoInerenteNome',
    false
  ),
  severidadeInerenteId: obterParametroUrlLista('severidadeInerenteId', false),
  severidadeInerenteNome: obterParametroUrlLista(
    'severidadeInerenteNome',
    false
  ),
  probabilidadeInerenteId: obterParametroUrlLista(
    'probabilidadeInerenteId',
    false
  ),
  probabilidadeInerenteNome: obterParametroUrlLista(
    'probabilidadeInerenteNome',
    false
  ),
  grausRiscoResidualId: obterParametroUrlLista('grausRiscoResidualId'),
  grausRiscoResidualNome: obterParametroUrlLista('grausRiscoResidualNome'),
  severidadeResidualId: obterParametroUrlLista('severidadeResidualId', false),
  severidadeResidualNome: obterParametroUrlLista(
    'severidadeResidualNome',
    false
  ),
  probabilidadeResidualId: obterParametroUrlLista(
    'probabilidadeResidualId',
    false
  ),
  probabilidadeResidualNome: obterParametroUrlLista(
    'probabilidadeResidualNome',
    false
  ),
  desconsiderarStatusConcluido: obterParametroUrl(
    'desconsiderarStatusConcluido'
  ),
  desconsiderarStatusCancelado: obterParametroUrl(
    'desconsiderarStatusCancelado'
  ),
  causasRiscoId: obterParametroUrlLista('causasRiscoId'),
  causasRiscoNome: obterParametroUrlLista('causasRiscoNome'),
  consequenciasRiscoId: obterParametroUrlLista('consequenciasRiscoId'),
  consequenciasRiscoNome: obterParametroUrlLista('consequenciasRiscoNome'),
  tratamentoId: obterParametroUrl('tratamentoId'),
  tratamentoNome: obterParametroUrl('tratamentoNome'),
  statusAvaliacaoId: obterParametroUrl('statusAvaliacaoId'),
  statusAvaliacaoNome: obterParametroUrl('statusAvaliacaoNome'),
  farolPontoControleId: obterParametroUrl('farolPontoControleId'),
  farolPontoControleNome: obterParametroUrl('farolPontoControleNome'),
});

const Risco = (props) => {
  const intl = useIntl();
  const { terms: resources, user, modules } = useAuth();

  const {
    params,
    showModal,
    showCustomFields,
    filtroDefault,
    dataSource,
    hideFiltroChip = false,
    ignoreFiltroId = false,
    disabled = false,
    isWidget = false,
    refreshColumns,
    ehItemImpactado = false,
    usoMatrizRisco,
    handleRemoveAssociation,
    handleSelectRisk,
    showColumns,
  } = props;

  const [saved, setSaved] = useState(0);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [isLoadingTiposSeveridades, setIsLoadingTiposSeveridades] =
    useState(true);
  const [closeModalState, setCloseModalState] = useState(false);
  const [parametroUrl, setParametroUrl] = useState(null);
  const [refreshFilterChip, setRefreshFilterChip] = useState(0);
  const [camposCustomizadosUrl] = useState(() =>
    obterParametrosPorPrefixo('campoCustomizado.')
  );
  const [camposMultiSelecaoEmConjuntoUrl] = useState(() =>
    Object.keys(obterParametrosPorPrefixo('multiSelecaoEmConjunto.'))
  );
  const [queryString, setQueryString] = useState(obterQueryStringInicial);
  const [configuracao, setConfiguracao] = useState(undefined);
  const [columns, setColumns] = useState(undefined);
  const [columnsDinamic, setColumnsDinamic] = useState(undefined);
  const [showColumnsState, setShowColumnsState] = useState(undefined);

  const changedColumns = 0;

  // O comentário fixado fica em ref porque as valueFunction das colunas são
  // criadas uma única vez (em createColumns) e precisam ler o valor atual, como
  // o `this.state` fazia na classe. O contador força o re-render.
  const [, forcarRender] = useState(0);
  const hintsRef = useRef({ comentarioFixadoId: null });
  const setHints = (patch) => {
    hintsRef.current = { ...hintsRef.current, ...patch };
    forcarRender((t) => t + 1);
  };

  const showColumn = (column) => {
    if (!showColumns || showColumns.length === 0) return true;

    return showColumns.includes(column);
  };

  const handleCustomColumnsOrder = (allColumns, colunas) => {
    const customColumns = allColumns.filter(
      (column) =>
        column?.visible &&
        showColumns.includes(
          column?.valueField
        ) /* || (column?.customFields && showColumns.includes(column?.valueField))*/
    );

    if (showColumns && showColumns.length > 0) {
      const newOrder = customColumns.sort((a, b) => {
        return (
          showColumns.indexOf(a?.valueField) -
          showColumns.indexOf(b?.valueField)
        );
      });

      return newOrder;
    }

    return colunas;
  };

  const obterIdPopoverTipoSeveridade = (item, index = 0) =>
    `colseveridade${index}${item.id}`;

  const obterIdPopoverComentarioSeveridade = (item, severidade, inerente) =>
    `col${severidade ? 'severidade' : 'probabilidade'}${
      inerente ? 'Inerente' : ''
    }${item.id}`;

  const fixarComentario = (id) => setHints({ comentarioFixadoId: id });

  const getTiposSeveridades = async () => {
    if (!showColumn('tipoSeveridade') || ehItemImpactado) {
      setIsLoadingTiposSeveridades(false);
      return;
    }

    try {
      await http
        .post(`/TipoSeveridade/List`, {
          parameters: { buscarExcluidos: false },
          pageSize: 1000,
        })
        .then((response) => {
          if (response?.data?.list?.length === 0)
            setIsLoadingTiposSeveridades(false);
          else {
            setColumnsDinamic(
              response.data.list.map((tipo, index) => ({
                headerText: tipo?.nome,
                visible: true,
                default: false,
                valueField: `severidade${index}`,
                valueFunction: (item) => {
                  const valorTipoSeveridade = item.valoresTipoSeveridade?.find(
                    (v) => v?.tipoSeveridade?.id == tipo.id
                  );

                  return (
                    <ComentarioCelula
                      id={obterIdPopoverTipoSeveridade(item, index)}
                      comentario={valorTipoSeveridade?.comentario}
                      fixadoId={hintsRef.current.comentarioFixadoId}
                      onFixar={fixarComentario}
                    >
                      {valorTipoSeveridade
                        ? valorTipoSeveridade.severidade?.nome
                        : '-'}
                    </ComentarioCelula>
                  );
                },
              }))
            );
            setIsLoadingTiposSeveridades(false);
          }
        });
    } catch {
      setIsLoadingTiposSeveridades(false);
    }
  };

  const atualizarParametros = (
    forceRefresh,
    queryStringAtual = queryString
  ) => {
    const {
      areaId,
      areaNome,
      areasSubordinadas,
      nome,
      statusId,
      statusDescricao,
      idIntegracao,
      processoId,
      processoNome,
      incluirSubprocessos,
      modeloRiscoId,
      modeloRiscoDescricao,
      matrizRiscoId,
      matrizRiscoDescricao,
      tipoRiscoId,
      tipoRiscoDescricao,
      responsavelId,
      responsavelNome,
      grausRiscoInerenteId,
      grausRiscoInerenteNome,
      severidadeInerenteId,
      severidadeInerenteNome,
      probabilidadeInerenteId,
      probabilidadeInerenteNome,
      grausRiscoResidualId,
      grausRiscoResidualNome,
      severidadeResidualId,
      severidadeResidualNome,
      probabilidadeResidualId,
      probabilidadeResidualNome,
      desconsiderarStatusConcluido,
      desconsiderarStatusCancelado,
      grauRiscoId,
      causasRiscoId,
      causasRiscoNome,
      consequenciasRiscoId,
      consequenciasRiscoNome,
      tratamentoId,
      tratamentoNome,
      statusAvaliacaoId,
      statusAvaliacaoNome,
      farolPontoControleId,
      farolPontoControleNome,
    } = queryStringAtual;

    if (
      forceRefresh ||
      (window.location.search && !window.location.pathname.includes('arvore'))
    ) {
      const novoParametroUrl = {
        areas: areaId
          ? Array.isArray(areaId)
            ? areaId.map((id, index) => {
                return { id, descricao: areaNome[index] };
              })
            : [{ id: areaId, descricao: areaNome ? areaNome : '' }]
          : null,
        areasSubordinadas: areasSubordinadas === 'true',
        nome,
        status: statusId ? { id: statusId, descricao: statusDescricao } : null,
        idIntegracao,
        processos: processoId
          ? Array.isArray(processoId)
            ? processoId.map((id, index) => {
                return { id, nome: processoNome[index] };
              })
            : [{ id: processoId, nome: processoNome ? processoNome : '' }]
          : null,
        incluirSubprocessos: incluirSubprocessos === 'true',
        modeloRisco: modeloRiscoId
          ? { id: modeloRiscoId, descricao: modeloRiscoDescricao }
          : null,
        matrizRisco: matrizRiscoId
          ? { id: matrizRiscoId, descricao: matrizRiscoDescricao }
          : null,
        grauRisco: grauRiscoId
          ? {
              id: grauRiscoId,
              descricao: grausRiscoResidualNome
                ? grausRiscoResidualNome[0]
                : '',
            }
          : null,
        tipoRisco: tipoRiscoId
          ? { id: tipoRiscoId, descricao: tipoRiscoDescricao }
          : null,
        responsaveis: responsavelId
          ? [
              {
                id: responsavelId,
                descricao: responsavelNome,
                nome: responsavelNome,
              },
            ]
          : null,
        grausRiscoInerente: grausRiscoInerenteId
          ? grausRiscoInerenteId.map((id, index) => {
              return { id: id, descricao: grausRiscoInerenteNome[index] };
            })
          : null,
        severidadeInerente: severidadeInerenteId
          ? severidadeInerenteId.map((id, index) => {
              return { id: id, nome: severidadeInerenteNome[index] };
            })
          : null,
        probabilidadeInerente: probabilidadeInerenteId
          ? probabilidadeInerenteId.map((id, index) => {
              return { id: id, nome: probabilidadeInerenteNome[index] };
            })
          : null,
        grausRiscoResidual: grausRiscoResidualId
          ? grausRiscoResidualId.map((id, index) => {
              return { id: id, descricao: grausRiscoResidualNome[index] };
            })
          : null,
        severidadeResidual: severidadeResidualId
          ? severidadeResidualId.map((id, index) => {
              return { id: id, nome: severidadeResidualNome[index] };
            })
          : null,
        probabilidadeResidual: probabilidadeResidualId
          ? probabilidadeResidualId.map((id, index) => {
              return { id: id, nome: probabilidadeResidualNome[index] };
            })
          : null,
        desconsiderarStatusConcluido: desconsiderarStatusConcluido === 'true',
        desconsiderarStatusCancelado: desconsiderarStatusCancelado === 'true',
        causasRisco: causasRiscoId
          ? causasRiscoId.map((id, index) => {
              return { id: id, descricao: causasRiscoNome[index] };
            })
          : null,
        consequenciasRisco: consequenciasRiscoId
          ? consequenciasRiscoId.map((id, index) => {
              return { id: id, descricao: consequenciasRiscoNome[index] };
            })
          : null,
        tratamento: tratamentoId
          ? { id: tratamentoId, descricao: tratamentoNome }
          : null,
        statusAvaliacao: statusAvaliacaoId
          ? { id: statusAvaliacaoId, descricao: statusAvaliacaoNome }
          : null,
        farolPontoControle: farolPontoControleId
          ? { id: farolPontoControleId, descricao: farolPontoControleNome }
          : null,
      };
      setParametroUrl(novoParametroUrl);
      setRefreshFilterChip((prev) => prev + 1);
    }
  };

  const encodeQueryData = (data) => {
    var ret = [];
    for (let d in data)
      if (data[d]) {
        ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
      }
    return ret.join('&');
  };

  const removeFilterUrl = (field) => {
    const data = {
      ...queryString,
      ...Object.entries(camposCustomizadosUrl).reduce(
        (obj, [key, value]) => ({
          ...obj,
          [`campoCustomizado.${key}`]: value,
        }),
        {}
      ),
    };

    const hasFilterUrl =
      data &&
      Object.prototype.hasOwnProperty.call(data, field) &&
      data[field] != null;

    if (field === 'status') {
      delete data.statusId;
      delete data.statusDescricao;
    } else if (field === 'responsaveis') {
      delete data.responsavelId;
      delete data.responsavelNome;
    } else if (field === 'areas') {
      delete data.areaId;
      delete data.areaNome;
    } else if (field === 'areasSubordinadas') {
      delete data.areasSubordinadas;
    } else if (field === 'processos') {
      delete data.processoId;
      delete data.processoNome;
    } else if (field === 'incluirSubprocessos') {
      delete data.incluirSubprocessos;
    } else if (field === 'modeloRisco') {
      delete data.modeloRiscoId;
      delete data.modeloRiscoDescricao;
    } else if (field === 'matrizRisco') {
      delete data.matrizRiscoId;
      delete data.matrizRiscoDescricao;
    } else if (field === 'tipoRisco') {
      delete data.tipoRiscoId;
      delete data.tipoRiscoDescricao;
    } else if (field === 'grausRiscoInerente') {
      delete data.grausRiscoInerenteId;
      delete data.grausRiscoInerenteNome;
    } else if (field === 'severidadeInerente') {
      delete data.severidadeInerenteId;
      delete data.severidadeInerenteNome;
    } else if (field === 'probabilidadeInerente') {
      delete data.probabilidadeInerenteId;
      delete data.probabilidadeInerenteNome;
    } else if (field === 'grausRiscoResidual') {
      delete data.grausRiscoResidualId;
      delete data.grausRiscoResidualNome;
    } else if (field === 'severidadeResidual') {
      delete data.severidadeResidualId;
      delete data.severidadeResidualNome;
    } else if (field === 'probabilidadeResidual') {
      delete data.probabilidadeResidualId;
      delete data.probabilidadeResidualNome;
    } else if (field === 'causasRisco') {
      delete data.causasRiscoId;
      delete data.causasRiscoNome;
    } else if (field === 'consequenciasRisco') {
      delete data.consequenciasRiscoId;
      delete data.consequenciasRiscoNome;
    } else if (field === 'tratamento') {
      delete data.tratamentoId;
      delete data.tratamentoNome;
    } else if (field === 'statusAvaliacao') {
      delete data.statusAvaliacaoId;
      delete data.statusAvaliacaoNome;
    } else if (field === 'farolPontoControle') {
      delete data.farolPontoControleId;
      delete data.farolPontoControleNome;
    }

    if (hasFilterUrl) {
      history.replace({
        pathname: `/gerenciar/risco`,
        search: encodeQueryData(data),
      });
    }

    setQueryString(data);
    atualizarParametros(true, data);
  };

  const closeModal = (salvo) => {
    if (props.ignoreCloseModal) return;

    if (salvo) {
      setCloseModalState(true);
      setSaved((prev) => prev + 1);
      props.refreshList && props.refreshList(salvo);
    } else {
      setCloseModalState(true);
    }
  };

  const refreshView = () => {
    setSaved((prev) => prev + 1);
    props.refreshList && props.refreshList(true);
  };

  const handleNomeClick = (item) => {
    ContentManager.addContent(
      <RiscoEditView
        mode="edit"
        handleClose={closeModal}
        item={item}
        location={props.location}
        showDelete={props.showDelete}
        disabledEdit={props.disabledEdit}
        afterSave={props.afterSave}
        urlList={props.urlList}
        parameters={props.parameters || params}
        model={props.model}
      />
    );
  };

  const showCreateRisk = () => {
    ContentManager.addContent(
      <RiscoEditView
        mode="edit"
        handleClose={refreshView}
        location={props.location}
        showDelete={props.showDelete}
        disabledEdit={props.disabledEdit}
        afterSave={props.afterSave}
        urlList={props.urlList}
        parameters={props.parameters || params}
        model={props.model}
      />
    );
  };

  const renderButtonCreateRisk = () => [
    <Button
      id={'botaoIdentificarRisco'}
      key={1}
      className="ml-2"
      onClick={() => showCreateRisk()}
      type="primary"
    >
      <LucideIcon icon="Plus" />
      <span className="ml-2">{capitalizeFirstLetter(resources.riscos)}</span>
    </Button>,
  ];

  const handleAssistenteVirtualClick = () => {
    const promptCausas = intl.formatMessage(
      { id: 'promptSugestaoRiscos' },
      { riscos: resources.riscos, risco: resources.risco }
    );

    const msgConsultoraVirtualActioDesvio = intl.formatMessage(
      {
        id: 'labelRiscos',
      },
      { riscos: capitalizeFirstLetter(resources.riscos) }
    );

    ContentManager.addContent(
      <AssistenteVirtual
        user={user}
        prompt={promptCausas}
        title={msgConsultoraVirtualActioDesvio}
        showCause={false}
        actionsToolBar={renderButtonCreateRisk()}
        interactionType={TIPO_INTERACAO_SOPHIE.SUGERIR_RISCO}
      />
    );
  };

  const renderActionsToolBar = () => [
    <div key="risco-actions-toolbar" style={{ maxHeight: '35px' }}>
      {handleSelectRisk && (
        <Button
          className="pull-right ml-2"
          leftIcon="magnify"
          type="primary"
          onClick={handleSelectRisk}
        >
          {intl.formatMessage(
            { id: 'labelVincularRisco' },
            { risco: resources.risco }
          )}
        </Button>
      )}
      <Tooltip
        key={1}
        placement="right"
        text={intl.formatMessage(
          { id: 'msgToolTipIdentificarRiscos' },
          { riscos: resources.riscos }
        )}
      >
        <Button
          className="ml-2"
          style={{ height: '35px' }}
          onClick={handleAssistenteVirtualClick}
          type="primary"
        >
          <SophieIcon color="#fff" size={15} />
          <span className="ml-2">
            {intl.formatMessage(
              { id: 'labelIdentificarRiscos' },
              {
                riscos: resources.riscos,
              }
            )}
          </span>
        </Button>
      </Tooltip>
    </div>,
  ];

  const renderNome = (item, callback) => {
    if (!item.nome) {
      return;
    }

    return (
      <a
        tabIndex="-1"
        onClick={() => handleNomeClick(item, callback)}
        style={{ cursor: 'pointer' }}
      >
        {item.nome}
      </a>
    );
  };

  const renderDescricao = (item) => (
    <div
      style={{
        maxHeight: '130px',
        alignSelf: 'flex-start',
      }}
    >
      {item.descricao}
    </div>
  );

  const renderStatus = (item) => {
    if (!item.status) return;

    return (
      <StatusBadge
        text={item.status?.descricao || '-'}
        textColor={item.status?.corFundo ? item.status?.cor : undefined}
        backgroundColor={item.status?.corFundo || item.status?.cor}
      />
    );
  };

  const renderLabelMenu = () => <LucideIcon icon="EllipsisVertical" />;

  const renderMenu = (item) => (
    <DropdownItemRisco
      showEdit={item.permissions && item.permissions.allowEdit}
      item={item}
      renderLabel={renderLabelMenu}
      handleEditClose={closeModal}
      location={props.location}
      showDelete={props.showDelete}
      disabledEdit={props.disabledEdit}
      afterSave={props.afterSave}
      urlList={props.urlList}
      parameters={props.parameters || params}
      model={props.model}
    />
  );

  const renderToleranciaRisco = (item) => {
    const { ratingToleranciaRisco } = item;
    if (!ratingToleranciaRisco) return;
    return (
      <div>
        <Badge
          pill
          className="text-white"
          style={{ background: ratingToleranciaRisco?.cor }}
        >
          {ratingToleranciaRisco?.descricao}
        </Badge>
      </div>
    );
  };

  const renderGrauRisco = (grauRisco, inerente, item) => {
    if (!(grauRisco && grauRisco.cor)) return;

    const grau = (
      <div>
        <LucideIcon
          icon="circle"
          color={grauRisco.cor}
          colorHover={grauRisco.cor}
          size={22}
        />
        {item.avaliacaoPendente && (
          <strong style={{ color: grauRisco.cor }} color={grauRisco.cor}>
            !
          </strong>
        )}
      </div>
    );

    if (!item.nome || !item.avaliacaoPendente) return grau;

    return (
      <HoverCard
        className="tw:w-75"
        content={
          <strong>
            {intl.formatMessage(
              { id: 'haAvaliacoesRiscoAtrasadas' },
              { risco: resources.risco }
            )}
          </strong>
        }
      >
        {grau}
      </HoverCard>
    );
  };

  const renderStatusAvaliacao = (item) => {
    if (!item.statusRiscoAvaliacao) return;

    const { statusRiscoAvaliacao } = item;

    return (
      <StatusBadge
        text={
          statusRiscoAvaliacao?.descricao
            ? intl.formatMessage({
                id: statusRiscoAvaliacao.descricao,
              })
            : '-'
        }
        textColor={
          statusRiscoAvaliacao?.corFundo ? statusRiscoAvaliacao?.cor : undefined
        }
        backgroundColor={
          statusRiscoAvaliacao?.corFundo || statusRiscoAvaliacao?.cor
        }
      />
    );
  };

  const renderDescricaoGrauRisco = (item, field = 'descricaoGrauRisco') =>
    item ? <div className=" text-center">{item[field]}</div> : <div></div>;

  const renderComentarioSeveridade = (item, inerente) => (
    <ComentarioCelula
      id={obterIdPopoverComentarioSeveridade(item, true, inerente)}
      comentario={
        inerente
          ? item?.comentarioSeveridadeInerente
          : item?.comentarioSeveridade
      }
      fixadoId={hintsRef.current.comentarioFixadoId}
      onFixar={fixarComentario}
    >
      {inerente ? item.severidadeInerente : item.severidade}{' '}
    </ComentarioCelula>
  );

  const renderComentarioProbabilidade = (item, inerente) => (
    <ComentarioCelula
      id={obterIdPopoverComentarioSeveridade(item, false, inerente)}
      comentario={
        inerente
          ? item?.comentarioProbabilidadeInerente
          : item?.comentarioProbabilidade
      }
      fixadoId={hintsRef.current.comentarioFixadoId}
      onFixar={fixarComentario}
    >
      {inerente ? item.probabilidadeInerente : item.probabilidade}{' '}
    </ComentarioCelula>
  );

  const renderRemoveAssociation = (item, index) => (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ width: 'calc(100% - 30px)' }}
    >
      <IconButton
        icon="close-circle"
        className="clarear cursor-pointer"
        style={{ cursor: 'pointer' }}
        onClick={() =>
          handleRemoveAssociation && handleRemoveAssociation(item, index)
        }
      />
    </div>
  );

  const renderTags = (item) =>
    item.tags?.map((x, index) => (
      <Badge
        key={x.id ?? x.descricao ?? index}
        pill
        className="text-white"
        style={{ background: x.cor }}
      >
        {x.descricao}
      </Badge>
    ));

  const renderCriterios = (item) => {
    if (!item.criterios || item.criterios.length === 0) return null;

    return (
      <div>
        {item.criterios &&
          item.criterios.map((itemCriterio) => (
            <div key={itemCriterio.id}>
              <span style={{ fontWeight: 'bold' }}>
                {itemCriterio.tabela}:{' '}
              </span>
              <span>{itemCriterio.descricao}</span>
            </div>
          ))}
      </div>
    );
  };

  const renderResponsavelAvaliacao = (item) => {
    return <div>{item.responsavelAvaliacao ?? '-'}</div>;
  };

  const createColumns = (configuracaoAtual) => {
    const hasGR = modules.some(
      (m) => m.id === MODULOS.GESTAO_RISCO && m.exibir
    );

    setColumns([
      <ListColumn
        headerText={intl.formatMessage({ id: 'nome' })}
        required
        minWidth={250}
        visible={showColumn('nome')}
        key="nome"
        valueField="nome"
        sortField={
          usoMatrizRisco ? 'Risco.UltimaVersao.Nome' : 'UltimaVersao.Nome'
        }
        valueFunction={renderNome}
        default
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'descricaoRisco' },
            { risco: resources.risco }
          )
        )}
        visible={showColumn('descricao')}
        key="descricao"
        valueField="descricao"
        sortField={
          usoMatrizRisco
            ? 'Risco.UltimaVersao.Descricao'
            : 'UltimaVersao.Descricao'
        }
        minWidth={200}
        wordBreak
        valueFunction={(item) => renderDescricao(item)}
        maxHeight="none"
      />,
      <ListColumn
        headerText={intl.formatMessage({ id: 'codigo' })}
        tooltip="Código de Importação"
        visible={showColumn('idIntegracao')}
        key="idIntegracao"
        valueField="idIntegracao"
        sortField={
          usoMatrizRisco
            ? 'Risco.UltimaVersao.IdIntegracao'
            : 'UltimaVersao.idIntegracao'
        }
        width={100}
        default={!ehItemImpactado}
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(resources.area)}
        key="area"
        valueField="area"
        visible={showColumn('area')}
        default={!ehItemImpactado}
        sortField="UltimaVersao.Area.Nome"
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(resources.responsaveis)}
        visible={showColumn('responsaveis')}
        key="responsaveis"
        valueField="responsaveis"
        default={!ehItemImpactado}
      />,
      <ListColumn
        headerText={intl.formatMessage(
          { id: 'label.severidade' },
          { severidade: resources.severidade }
        )}
        key="severidade"
        valueField="severidade"
        visible={showColumn('severidade')}
        sortField="UltimaVersao.SeveridadeResidual.Nome"
        valueFunction={(item) => renderComentarioSeveridade(item, false)}
        default
      />,
      <ListColumn
        headerText={resources.probabilidade}
        key="probabilidade"
        valueField="probabilidade"
        visible={showColumn('probabilidade')}
        valueFunction={(item) => renderComentarioProbabilidade(item, false)}
        sortField="UltimaVersao.ProbabilidadeResidual.Nome"
        default
      />,
      <ListColumn
        headerText={intl.formatMessage(
          { id: 'labelGrauRiscoResidual' },
          { risco: resources.risco }
        )}
        key="grauRisco"
        valueField="grauRisco"
        visible={showColumn('grauRisco')}
        valueFunction={(item) =>
          renderGrauRisco(item && item.grauRisco, false, item)
        }
        default
      />,
      <ListColumn
        headerText={`${intl.formatMessage({
          id: 'descricao',
        })} ${intl.formatMessage(
          { id: 'labelGrauRiscoResidual' },
          { risco: resources.risco }
        )}`}
        visible={showColumn('descricaoGrauRisco')}
        key="descricaoGrauRisco"
        valueField="descricaoGrauRisco"
        valueFunction={(item) =>
          renderDescricaoGrauRisco(item, 'descricaoGrauRisco')
        }
        default={!ehItemImpactado}
      />,
      <ListColumn
        headerText={intl.formatMessage(
          { id: 'label.periodicidadeAvaliacao' },
          { avaliacao: resources.avaliacao }
        )}
        visible={showColumn('periodicidadeAvaliacao')}
        key="periodicidadeAvaliacao"
        valueField="periodicidadeAvaliacao"
        default
      />,
      <ListColumn
        headerText={intl.formatMessage({ id: 'status' })}
        visible={showColumn('status')}
        key="status"
        valueField="status"
        sortField="UltimaVersao.status"
        valueFunction={renderStatus}
        default
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(resources.pontodecontrole)}
        visible={hasGR && showColumn('descricoesPontoControle')}
        key="descricoesPontoControle"
        valueField="descricoesPontoControle"
        minWidth={300}
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelStatusAvaliacao' })
        )}
        visible={showColumn('statusRiscoAvaliacao')}
        key="statusRiscoAvaliacao"
        valueField="statusRiscoAvaliacao"
        valueFunction={renderStatusAvaliacao}
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(resources.processos)}
        visible={hasGR && showColumn('descricoesProcessos')}
        key="descricoesProcessos"
        valueField="descricoesProcessos"
        default
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'labelCausasDoRisco' },
            {
              pontosproblematicos: resources.pontosproblematicos,
              risco: resources.risco,
            }
          )
        )}
        visible={showColumn('descricoesCausas')}
        key="descricoesCausas"
        valueField="descricoesCausas"
        minWidth={300}
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'labelConsequenciasDoRisco' },
            {
              consequencias: resources.consequencias,
              risco: resources.risco,
            }
          )
        )}
        visible={showColumn('descricoesConsequencias')}
        key="descricoesConsequencias"
        valueField="descricoesConsequencias"
        minWidth={300}
      />,

      <ListColumn
        headerText={intl.formatMessage({ id: 'pontuacao' })}
        visible={showColumn('score')}
        key="score"
        valueField="score"
        valueFunction={(item) => (item.possuiAvaliacao ? item.score : '')}
        sortField="UltimaVersao.Score"
        default
      />,
      configuracaoAtual?.avaliarRiscoInerente && (
        <ListColumn
          headerText={intl.formatMessage(
            { id: 'labelGrauRiscoInerente' },
            { risco: resources.risco }
          )}
          visible={showColumn('grauRiscoInerente')}
          key="grauRiscoInerente"
          valueField="grauRiscoInerente"
          valueFunction={(item) =>
            renderGrauRisco(item && item.grauRiscoInerente, true, item)
          }
          default
        />
      ),
      configuracaoAtual?.avaliarRiscoInerente && (
        <ListColumn
          headerText={`${intl.formatMessage({
            id: 'descricao',
          })} ${intl.formatMessage(
            { id: 'labelGrauRiscoInerente' },
            { risco: resources.risco }
          )}`}
          visible={showColumn('descricaoGrauRiscoInerente')}
          key="descricaoGrauRiscoInerente"
          valueField="descricaoGrauRiscoInerente"
          valueFunction={(item) =>
            renderDescricaoGrauRisco(item, 'descricaoGrauRiscoInerente')
          }
          default={!ehItemImpactado}
        />
      ),
      configuracaoAtual?.avaliarRiscoInerente && (
        <ListColumn
          headerText={intl.formatMessage({ id: 'labelScoreInerente' })}
          visible={showColumn('scoreInerente')}
          key="scoreInerente"
          valueField="scoreInerente"
          valueFunction={(item) =>
            item.possuiAvaliacao ? item.scoreInerente : ''
          }
          default={!ehItemImpactado}
        />
      ),
      configuracaoAtual?.avaliarRiscoInerente && (
        <ListColumn
          headerText={intl.formatMessage(
            { id: 'labelSeveridadeInerente' },
            { severidade: resources.severidade }
          )}
          visible={showColumn('severidadeInerente')}
          key="severidadeInerente"
          valueField="severidadeInerente"
          valueFunction={(item) => renderComentarioSeveridade(item, true)}
          default={!ehItemImpactado}
        />
      ),
      configuracaoAtual?.avaliarRiscoInerente && (
        <ListColumn
          headerText={intl.formatMessage(
            { id: 'labelProbabilidadeInerente' },
            { probabilidade: resources.probabilidade }
          )}
          visible={showColumn('probabilidadeInerente')}
          key="probabilidadeInerente"
          valueField="probabilidadeInerente"
          valueFunction={(item) => renderComentarioProbabilidade(item, true)}
          default={!ehItemImpactado}
        />
      ),
      configuracaoAtual?.habilitarDicionarioRiscos && (
        <ListColumn
          headerText={intl.formatMessage(
            { id: 'labelCategoriaDicionarioRisco' },
            { riscos: resources.riscos }
          )}
          visible={showColumn('categoriaDicionarioRisco')}
          key="categoriaDicionarioRisco"
          valueField="categoriaDicionarioRisco"
          default
        />
      ),
      ...(configuracaoAtual?.habilitarAcompanhamentoToleranciaRisco
        ? [
            <ListColumn
              headerText={intl.formatMessage(
                { id: 'labelToleranciaRisco' },
                { risco: resources.risco }
              )}
              visible={showColumn('toleranciaRisco')}
              key="toleranciaRisco"
              valueField="toleranciaRisco"
              default
            />,
            <ListColumn
              headerText={intl.formatMessage(
                { id: 'labelToleranciaRiscoFaixas' },
                { risco: resources.risco }
              )}
              visible={showColumn('ratingToleranciaRisco')}
              key="ratingToleranciaRisco"
              valueField="ratingToleranciaRisco"
              valueFunction={(item) => renderToleranciaRisco(item)}
              default
            />,
          ]
        : []),
      <ListColumn
        headerText={intl.formatMessage({
          id: 'impactoFinanceiroResidualEstimado',
        })}
        //sortField="impactoFinanceiroResidualEstimado"
        valueFunction={(item) =>
          item.impactoFinanceiroResidualEstimado ? (
            <div>
              {configuracaoAtual?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
              {formatNumber(item.impactoFinanceiroResidualEstimado, 2)}
            </div>
          ) : (
            <>-</>
          )
        }
        visible={
          configuracaoAtual?.acompanharImpactoFinanceiroEventoRisco &&
          showColumn('impactoFinanceiroResidualEstimado')
        }
        key="impactoFinanceiroResidualEstimado"
        valueField="impactoFinanceiroResidualEstimado"
      />,
      <ListColumn
        headerText={intl.formatMessage({
          id: 'impactoFinanceiroInerenteEstimado',
        })}
        //sortField="impactoFinanceiroInerenteEstimado"
        valueFunction={(item) =>
          item.impactoFinanceiroInerenteEstimado ? (
            <div>
              {configuracaoAtual?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
              {formatNumber(item.impactoFinanceiroInerenteEstimado, 2)}
            </div>
          ) : (
            <>-</>
          )
        }
        visible={
          configuracaoAtual?.avaliarRiscoInerente &&
          configuracaoAtual?.acompanharImpactoFinanceiroEventoRisco &&
          showColumn('impactoFinanceiroInerenteEstimado')
        }
        key="impactoFinanceiroInerenteEstimado"
        valueField="impactoFinanceiroInerenteEstimado"
      />,
      <ListColumn
        headerText={`
                  ${intl.formatMessage({
                    id: 'reducaoImpactoFinanceiroRealizado',
                  })} ${
          configuracaoAtual?.unidadeMedidaImpactoFinanceiro?.sigla
            ? `(${configuracaoAtual?.unidadeMedidaImpactoFinanceiro?.sigla})`
            : ''
        }`}
        sortField="reducaoImpactoFinanceiroRealizado"
        valueFunction={(item) =>
          item.reducaoImpactoFinanceiroRealizado ? (
            <div>
              {configuracaoAtual?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
              {formatNumber(item.reducaoImpactoFinanceiroRealizado, 2)}
            </div>
          ) : (
            <>-</>
          )
        }
        visible={
          configuracaoAtual?.avaliarRiscoInerente &&
          configuracaoAtual?.acompanharImpactoFinanceiroEventoRisco &&
          showColumn('reducaoImpactoFinanceiroRealizado')
        }
        key="reducaoImpactoFinanceiroRealizado"
        valueField="reducaoImpactoFinanceiroRealizado"
      />,
      <ListColumn
        headerText={intl.formatMessage({
          id: 'reducaoImpactoFinanceiroPercentual',
        })}
        sortField="reducaoImpactoFinanceiroPercentual"
        valueFunction={(item) =>
          item.reducaoImpactoFinanceiroPercentual ? (
            <div>
              {formatNumber(item.reducaoImpactoFinanceiroPercentual, 2)} %
            </div>
          ) : (
            <>-</>
          )
        }
        visible={
          configuracaoAtual?.avaliarRiscoInerente &&
          configuracaoAtual?.acompanharImpactoFinanceiroEventoRisco &&
          showColumn('reducaoImpactoFinanceiroPercentual')
        }
        key="reducaoImpactoFinanceiroPercentual"
        valueField="reducaoImpactoFinanceiroPercentual"
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(resources.tipoRisco)}
        key="tipoRisco"
        valueField="tipoRisco"
        visible={showColumn('tipoRisco')}
        default
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'tratamento' })
        )}
        key="tratamentoRisco"
        valueField="tratamentoRisco"
        visible={showColumn('tratamentoRisco')}
        default
      />,
      <ListColumn
        headerText={intl.formatMessage(
          { id: 'tags' },
          { tags: resources.tags }
        )}
        key="tags"
        valueField="tags"
        valueFunction={renderTags}
        visible={configuracaoAtual?.podeVisualizarTag && showColumn('tags')}
        default={!ehItemImpactado}
      />,
      configuracaoAtual?.exibirColunaCriterios && !ehItemImpactado && (
        <ListColumn
          headerText={intl.formatMessage(
            { id: 'labelCriteriosProbabilidade' },
            { probabilidade: resources.probabilidade }
          )}
          key="criterios"
          valueField="criterios"
          valueFunction={renderCriterios}
        />
      ),
      configuracaoAtual?.utilizarClassificacaoRisco && !ehItemImpactado && (
        <ListColumn
          headerText={intl.formatMessage({
            id: 'label.classificacao',
          })}
          key="classificacaoRisco"
          valueField="classificacaoRisco"
        />
      ),
      handleRemoveAssociation && (
        <ListColumn
          headerText={intl.formatMessage({ id: 'labelRemoverAssociacao' })}
          required
          width={160}
          visible={true}
          key="removeAssociation"
          valueField="removeAssociation"
          valueFunction={(item) => renderRemoveAssociation(item)}
          default
        />
      ),
      <ListColumn
        headerText={intl.formatMessage(
          { id: 'label.responsavelAvaliacao' },
          {
            avaliacao: resources.avaliacao,
          }
        )}
        width={160}
        visible={showColumn('responsavelAvaliacao')}
        key="responsavelAvaliacao"
        valueField="responsavelAvaliacao"
        valueFunction={(item) => renderResponsavelAvaliacao(item)}
      />,
    ]);
    setIsLoadingConfigs(false);
  };

  const getMergeColumns = () => {
    // `severidadesColumns` nunca era preenchido na versão em classe, então
    // apenas o caminho abaixo era executado.
    let newColumns = columns.filter((column) => column);

    if (showColumnsState && showColumnsState.length > 0) {
      newColumns = columns.sort((a, b) => {
        return (
          showColumnsState.indexOf(a.props?.valueField) -
          showColumnsState.indexOf(b.props?.valueField)
        );
      });
    }

    return newColumns;
  };

  useEffect(() => {
    if (props.configuracao) {
      setConfiguracao(props.configuracao);
    } else {
      getConfiguration('ItemConfiguracaoRisco')(setConfiguracao)();
    }

    getTiposSeveridades();
    atualizarParametros();
  }, []);

  // equivale ao callback do setState({ configuracao }, () => this.createColumns())
  useEffect(() => {
    if (!configuracao) return;

    createColumns(configuracao);
  }, [configuracao]);

  // equivale ao UNSAFE_componentWillReceiveProps
  const primeiroRenderColunas = useRef(true);
  useEffect(() => {
    if (primeiroRenderColunas.current) {
      primeiroRenderColunas.current = false;
      return;
    }

    setShowColumnsState(showColumns);
  }, [refreshColumns, JSON.stringify(showColumns)]);

  const primeiraSincronizacaoColunas = useRef(true);
  useEffect(() => {
    if (primeiraSincronizacaoColunas.current) {
      primeiraSincronizacaoColunas.current = false;
      return;
    }

    createColumns(configuracao);
  }, [showColumnsState]);

  const filterUrl = parametroUrl ? true : false;

  return (
    <LoadingContainer isLoading={isLoadingConfigs || isLoadingTiposSeveridades}>
      {!(isLoadingConfigs && isLoadingTiposSeveridades) &&
        configuracao &&
        resources.area &&
        columns && (
          <List
            url="/Risco"
            showCustomFields={showCustomFields}
            columnsDinamic={columnsDinamic}
            tipoItem={6}
            //urlEdit="/gerenciar/risco/edit"
            sort="UltimaVersao.Nome"
            searchComponent={
              showModal ? (
                <Search configuracao={configuracao} />
              ) : (
                <Search configuracao={configuracao} />
              )
            }
            renderMenuAddOptions={true}
            renderMenu={renderMenu}
            filterDrawer={true}
            showDelete={false}
            searchSchema={showModal ? SearchSchemaModal : SearchSchema}
            pageSize={20}
            key="nome"
            valueField="nome"
            hideFiltroChip={hideFiltroChip}
            changedColumns={refreshColumns ? refreshColumns : changedColumns}
            filtroId={
              ignoreFiltroId
                ? 0
                : showModal
                ? props.filtroId
                  ? props.filtroId
                  : 302
                : 301
            }
            showSearch={props.showSearch ? props.showSearch : true}
            tag={
              props.ignoreTag
                ? null
                : showModal
                ? 2011
                : isWidget
                ? 12031
                : ehItemImpactado
                ? COLUNAS.RISCOS_ITENS_IMPACTADOS
                : 2010
            }
            handleCustomColumnsOrder={
              isWidget ? handleCustomColumnsOrder : null
            }
            selectedFilter={filtroDefault && { valor: filtroDefault }}
            parameters={params}
            saved={props.saved ? props.saved : saved}
            editComponent={RiscoEditView}
            closeModal={closeModalState}
            handleClose={closeModal}
            showNew={!disabled}
            newButtonTestId="gerenciar-risco-new-button"
            modelValue={filtroDefault ? filtroDefault : parametroUrl}
            dataSource={dataSource}
            refreshFilterChip={props.refreshFilterChip + refreshFilterChip}
            onRemoveFilterChip={filterUrl && removeFilterUrl}
            openLastFilter={filterUrl ? false : true}
            actionsToolBarEnd={renderActionsToolBar()}
            camposCustomizadosUrl={camposCustomizadosUrl}
            camposMultiSelecaoEmConjuntoUrl={camposMultiSelecaoEmConjuntoUrl}
            showEdit={false}
            {...props}
          >
            {getMergeColumns()}
          </List>
        )}
      {/* {!showModal && <Legenda />} */}
    </LoadingContainer>
  );
};

export default Risco;
